import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box } from '@wix/design-system';
import { useUnknownChargeContext } from '../../providers/UnknownChargeProvider';
import {
  formStateReducer,
  getInitialState,
} from '../../reducers/formStateReducer';
import { SearchButton } from '../SearchButton/SearchButton';
import s from './Form.scss';
import { allFieldsAreValid, allFieldsNotEmpty } from './validators';
import { useClientApi } from '../../hooks/useClientApi';
import { useSentry } from '../../providers/ErrorsProvider';
import {
  formSubmissionReducer,
  initialState as submitInitialState,
} from '../../reducers/formSubmissionReducer';
import { PremiumAssetType } from '@wix/ambassador-premium-billing-and-payments/types';
import {
  getErrorMessage,
  getGrpsApplicationErrorCode,
  getNoNetworkError,
} from './errorsHandler';
import { InvoiceNumberField } from './Fields/InvoiceNumberField';
import { LastFourDigitsField } from './Fields/LastFourDigitsField';
import { ExpirationField } from './Fields/ExpirationField';
import { SearchNotFound } from './SearchNotFound';
import { useBiLogger } from '../../providers/BiLoggerProvider';

export const Form: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    setChargeInfo,
    setPageErrors,
    chargeDetails,
  } = useUnknownChargeContext();
  const { clientApi } = useClientApi();
  const { biLogger } = useBiLogger();
  const { sentryClient } = useSentry();
  const [formState, setFormState] = useReducer(
    formStateReducer,
    getInitialState(),
  );
  const { expirationDate, invoiceNumber, lastFourDigits } = formState;
  const [{ canSubmit, formSubmission }, setSubmitState] = useReducer(
    formSubmissionReducer,
    submitInitialState,
  );
  const searchNotFound =
    chargeDetails.service === PremiumAssetType.UNKNOWN &&
    formSubmission === 'success';

  useEffect(
    () =>
      allFieldsAreValid(formState) && allFieldsNotEmpty(formState)
        ? setSubmitState({ type: 'canSubmit', value: true })
        : setSubmitState({ type: 'canSubmit', value: false }),
    [formState],
  );

  useEffect(() => {
    setFormState({ type: 'clearAllFields', value: formState });
  }, []);

  const onSubmit = async (captchaToken: string) => {
    try {
      biLogger.clickSearch({ invoiceNumber: invoiceNumber.value });
      const payload = await clientApi.locateInvoice({
        captchaToken,
        creditCard: {
          lastFourDigits: lastFourDigits.value,
          expiration: {
            month: expirationDate.month,
            year: expirationDate.year,
          },
        },
        invoiceNumber: invoiceNumber.value,
      });

      setChargeInfo(payload?.data?.chargeInfo || {});
      setSubmitState({ type: 'formSubmission', value: 'success' });

      if (payload?.data?.chargeInfo?.invoiceNumber) {
        navigate('/billing/unknown-charge/charge-details');
      }
    } catch (e: unknown) {
      const error = getGrpsApplicationErrorCode(e) || getNoNetworkError(e);
      setSubmitState({
        type: 'formSubmission',
        value: 'failed',
      });
      biLogger.logError({ errorInfo: t(getErrorMessage(error)) });
      setPageErrors({ hasError: true, message: t(getErrorMessage(error)) });
      sentryClient.captureException(e);
    }
  };

  return (
    <Box className={s.formWrapper}>
      <Box className={s.invoiceNumber} display="block">
        <InvoiceNumberField
          setState={setFormState}
          value={formState.invoiceNumber}
        />
      </Box>
      <Box className={s.inputWithLabel} display="block">
        <LastFourDigitsField
          setState={setFormState}
          value={formState.lastFourDigits}
        />
      </Box>
      <Box className={s.inputWithLabel} display="block">
        <ExpirationField value={expirationDate} setState={setFormState} />
      </Box>
      {searchNotFound && (
        <Box className={s.notFound} display="block">
          <SearchNotFound invoiceNumber={invoiceNumber.value} />
        </Box>
      )}
      <Box className={s.submitButton}>
        <SearchButton
          onSubmit={onSubmit}
          disabled={!canSubmit}
          formSubmission={formSubmission}
          setFormField={setSubmitState}
        />
      </Box>
    </Box>
  );
};
